import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CategoryFilter, EquipmentCard } from '@components';
import { Search, ArrowRight, Star } from 'lucide-react';
import { equipmentService } from '../services/equipmentService';
import { Equipment } from '../types/equipment';
import { getImageUrl } from '../utils/imageUrl';

const HomePage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();

  // const featuredEquipment = mockEquipments.slice(0, 8);
  const [featuredEquipment, setFeaturedEquipment] = useState<Equipment[]>([]);

  useEffect(() => {
    equipmentService.getAllEquipments().then(equipments => {
      setFeaturedEquipment(equipments.slice(0, 8).map(equipment => ({
        ...equipment,
        images: equipment.images.map(image => ({
          ...image,
          url: getImageUrl(image.url),
        })),
      })));
    });
  }, []);

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-blue-900/80 to-blue-600/60"></div>
        <img
          src="/fixionityHeroImage.png"
          alt="Person using rehabilitation equipment"
          className="w-full h-[300px] md:h-[400px] object-cover"
        />
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 text-center text-white">
          <h1 className="mb-6 text-3xl font-bold md:text-5xl drop-shadow-lg">
            Encuentra el equipo de rehabilitación perfecto
          </h1>
          <p className="max-w-2xl mb-2 text-lg md:text-xl">
            Explora por categoría
          </p>
          <CategoryFilter
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            color='white'
          />
        </div>
      </div>
      {/* Featured Equipment Section */}
      <div className="container px-4 py-12 mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Equipos Destacados
          </h2>
          <button
            onClick={() => navigate('/search')}
            className="flex items-center font-medium text-blue-600 hover:text-blue-800"
          >
            <span className="mr-1">Ver todos</span>
            <ArrowRight size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {featuredEquipment.map((equipment) => (
            <EquipmentCard key={equipment.id} equipment={equipment} />
          ))}
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-12 bg-blue-50">
        <div className="container px-4 mx-auto">
          <h2 className="mb-12 text-2xl font-bold text-center text-gray-800">
            Cómo Funciona Fixionity
          </h2>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="flex flex-col items-center text-center">
              <div className="flex items-center justify-center w-16 h-16 mb-4 bg-blue-100 rounded-full">
                <Search size={30} className="text-blue-600" />
              </div>
              <h3 className="mb-2 text-xl font-semibold">Buscar Equipos</h3>
              <p className="text-gray-600">
                Busca el equipo de rehabilitación que necesitas por ubicación, tipo y disponibilidad.
              </p>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="flex items-center justify-center w-16 h-16 mb-4 bg-blue-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-blue-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold">Reservar</h3>
              <p className="text-gray-600">
                Selecciona tus fechas de alquiler, confirma la disponibilidad y reserva tu equipo de forma segura en línea.
              </p>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="flex items-center justify-center w-16 h-16 mb-4 bg-blue-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-blue-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold">Recoger y Recuperate</h3>
              <p className="text-gray-600">
                Recoge tu equipo en la ubicación acordada, comienza tu rehabilitación y devuélvelo al finalizar el periodo de alquiler.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => navigate('/search')}
              className="px-6 py-3 font-medium text-white bg-blue-600 rounded-lg btn-primary hover:bg-blue-700"
            >
              Encuentra Equipos Ahora
            </button>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="container px-4 py-12 mx-auto">
        <h2 className="mb-12 text-2xl font-bold text-center text-gray-800">
          Testimonios de Usuarios
        </h2>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div className="p-6 bg-white shadow-md rounded-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 mr-4 overflow-hidden rounded-full">
                <img
                  src="https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=150"
                  alt="User"
                  className="object-cover w-full h-full"
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Sarah Johnson</h3>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              "Después de mi cirugía de rodilla, necesitaba una máquina CPM para mi recuperación. RehabRent facilitó encontrar una localmente a un precio asequible. El proceso fue fluido y ayudó a acelerar mi rehabilitación."
            </p>
          </div>

          <div className="p-6 bg-white shadow-md rounded-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 mr-4 overflow-hidden rounded-full">
                <img
                  src="https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150"
                  alt="User"
                  className="object-cover w-full h-full"
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Michael Torres</h3>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className={i < 4 ? "text-yellow-400 fill-yellow-400" : "text-gray-300"} />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              "Como fisioterapeuta, coloco algunos de mis equipos en RehabRent. Es una excelente manera de ayudar a más pacientes a acceder a equipos de rehabilitación de calidad, y la plataforma hace que la gestión de alquileres sea muy sencilla."
            </p>
          </div>

          <div className="p-6 bg-white shadow-md rounded-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 mr-4 overflow-hidden rounded-full">
                <img
                  src="https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=150"
                  alt="User"
                  className="object-cover w-full h-full"
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Emily Wong</h3>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              "Necesitaba un scooter de movilidad para mi madre que estaba de visita. Encontré uno cerca a través de RehabRent a la mitad del costo de comprar uno nuevo. El propietario incluso lo entregó en nuestra casa. ¡Servicio excepcional!"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;